import { useCallback, useEffect, useRef, useState } from "react";
import { getChargingStationInfo, unlockConnector } from "../services/apiService";
import analyticsService from "../services/analyticsService";

// Temps laissé au client pour retirer son câble avant de lui proposer de réessayer.
export const UNLOCK_CONFIRM_TIMEOUT_MS = 60 * 1000;
export const UNLOCK_STATUS_POLL_MS = 3000;

// La prise est libre une fois le câble retiré.
const RELEASED_STATUS = "Available";

// Déblocage du câble en fin de recharge (QCT-670).
//
// QoREST transmet l'ordre UnlockConnector à la borne sans en garder la réponse :
// l'appel ne renvoie que « commande envoyée ». Le seul signal fiable côté front
// est le statut de la prise, qui passe à Available quand le câble est retiré.
// On n'annonce donc jamais « câble débloqué » sur la seule foi de l'appel :
//   - sent        : ordre envoyé, en attente du retrait du câble ;
//   - released    : la prise est libre, le câble a été retiré ;
//   - stillLocked : rien au bout du délai, ou l'ordre n'a pas pu partir.
export default function useConnectorUnlock({ evcpReference, connectorNumber, accessToken }) {
  const [status, setStatus] = useState("idle");
  const [attempt, setAttempt] = useState(0);

  const paramsRef = useRef({});
  paramsRef.current = { evcpReference, connectorNumber, accessToken };
  const reportStuckRef = useRef(() => {});

  useEffect(() => {
    if (!attempt) return undefined;

    const { evcpReference: evcp, connectorNumber: number, accessToken: token } =
      paramsRef.current;
    const analyticsParams = { station_id: evcp, connector_number: number, attempt };
    let stopped = false;
    let pollTimer = null;
    let timeoutTimer = null;

    const stop = () => {
      stopped = true;
      clearTimeout(pollTimer);
      clearTimeout(timeoutTimer);
    };

    const finish = (nextStatus, event, extra = {}) => {
      if (stopped) return;
      stop();
      setStatus(nextStatus);
      analyticsService.pushEvent(event, { ...analyticsParams, ...extra });
    };

    const pollConnector = async () => {
      try {
        const station = await getChargingStationInfo(evcp);
        if (stopped) return;

        const connector = station?.connectors?.find((c) => c.number === number);
        if (connector?.status === RELEASED_STATUS) {
          finish("released", "cable_unlocked");
          return;
        }
      } catch (error) {
        // Erreur passagère : le délai tranchera si elle dure.
      }

      if (!stopped) {
        pollTimer = setTimeout(pollConnector, UNLOCK_STATUS_POLL_MS);
      }
    };

    // Le client signale lui-même que le câble ne vient pas : inutile d'attendre le délai.
    reportStuckRef.current = () => finish("stillLocked", "cable_unlock_failed", { error_type: "reported" });

    setStatus("sending");
    analyticsService.pushEvent("cable_unlock_requested", analyticsParams);

    unlockConnector(evcp, number, token)
      .then(() => {
        if (stopped) return;
        setStatus("sent");
        timeoutTimer = setTimeout(
          () => finish("stillLocked", "cable_unlock_failed", { error_type: "timeout" }),
          UNLOCK_CONFIRM_TIMEOUT_MS
        );
        pollConnector();
      })
      .catch((error) =>
        finish("stillLocked", "cable_unlock_failed", {
          error_type: "request",
          ...(error?.status ? { http_status: error.status } : {}),
        })
      );

    return stop;
  }, [attempt]);

  // Premier clic comme « Réessayer » : chaque tentative renvoie l'ordre.
  const unlock = useCallback(() => {
    setAttempt((current) => current + 1);
  }, []);

  const reportStuck = useCallback(() => reportStuckRef.current(), []);

  return { status, unlock, reportStuck };
}
